import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useChat } from '../../../context/ChatContext';
import { useTranslation } from 'react-i18next';

const MessageItem = ({ conv, onClick }: { conv: any, onClick: () => void }) => {
    const { t } = useTranslation();
    const [imageError, setImageError] = useState(false);
    
    // Construct full image URL if it's a relative path
    const profilePictureUrl = conv.otherUser.profilePicture 
        ? (conv.otherUser.profilePicture.startsWith('http') 
            ? conv.otherUser.profilePicture 
            : `${process.env.REACT_APP_API_URL}${conv.otherUser.profilePicture}`)
        : null;

    const initials = `${conv.otherUser.firstName?.charAt(0) || ''}${conv.otherUser.lastName?.charAt(0) || ''}`.toUpperCase() || 'M';

    const getLastMessageText = () => {
        if (!conv.lastMessage) return t('Messages.noMessages', 'No hay mensajes aún');
        
        if (conv.lastMessage.messageType === 'schedule_request' || conv.lastMessage.text === 'Solicitud de horario enviada') {
            return t('Messages.scheduleRequestSent', 'Solicitud de horario enviada');
        }
        
        return conv.lastMessage.text;
    };

    return (
        <button
            className="bw-menu-item"
            type="button"
            onClick={onClick}
        >
            {profilePictureUrl && !imageError ? (
                <img 
                    className="bw-menu-avatar" 
                    src={profilePictureUrl} 
                    alt={conv.otherUser.firstName} 
                    onError={() => setImageError(true)}
                />
            ) : (
                <span className="bw-menu-icon notranslate" translate="no" style={{ background: '#e5ede7', color: '#4b705c' }}>
                    {initials}
                </span>
            )}
            <span className="bw-menu-copy">
                <strong>{conv.otherUser.firstName} {conv.otherUser.lastName}</strong>
                <span>{getLastMessageText()}</span>
            </span>
            {conv.lastMessage && !conv.lastMessage.isRead && !conv.lastMessage.isMine && (
                <span className="bw-unread"></span>
            )}
        </button>
    );
};

export const MessagesDropdown: React.FC = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { unreadCount, conversations, setCurrentConversationId, toggleChatDrawer, isChatDrawerOpen } = useChat();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleConversationClick = (convId: number) => {
        setCurrentConversationId(convId);
        if (!isChatDrawerOpen) {
            toggleChatDrawer();
        }
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                className={classNames('bw-header-icon', { 'is-active': isOpen || isChatDrawerOpen })}
                onClick={() => setIsOpen(!isOpen)}
                aria-label={t('Messages.title', 'Mensajes')}
                aria-expanded={isOpen}
            >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 5.5h16v11H8l-4 3v-14Z"></path>
                </svg>
                {unreadCount > 0 && (
                    <span className="bw-header-badge">
                        {unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="bw-header-menu">
                    <div className="bw-header-menu-title">
                        <strong>{t('Messages.title', 'Mensajes')}</strong>
                        <span>{t('Messages.conversationsCount', '{{count}} conversaciones', { count: conversations.length })}</span>
                    </div>

                    {conversations.length > 0 ? (
                        conversations.map((conv) => (
                            <MessageItem 
                                key={conv.id} 
                                conv={conv} 
                                onClick={() => handleConversationClick(conv.id)} 
                            />
                        ))
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', fontSize: '10px' }}>
                            {t('Messages.noConversations', 'No tienes conversaciones.')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
